<div class="support-page">
  <!-- Breadcrumb -->
  <nav class="breadcrumb" aria-label="Breadcrumb">
    <a routerLink="/" class="breadcrumb__home">
      <span aria-hidden="true">⌂</span>
    </a>
    <span class="breadcrumb__divider">/</span>
    <span class="breadcrumb__current">Help &amp; Support</span>
  </nav>

  <!-- Hero -->
  <section class="support-hero">
    <div class="support-hero__background" aria-hidden="true">
      <span class="hero-orb hero-orb--one"></span>
      <span class="hero-orb hero-orb--two"></span>
      <span class="hero-grid"></span>
    </div>

    <div class="support-hero__content">
      <span class="hero-pill">
        <span class="hero-pill__dot"></span>
        gloget Support Centre
      </span>

      <h1>How can we help you?</h1>

      <p>
        Have a question, need assistance with a booking, or experiencing an
        issue? We're here to help you get back on track.
      </p>

      <div class="support-hero__actions">
        <button
          type="button"
          class="hero-button hero-button--primary"
          (click)="scrollToForm()"
        >
          Contact Support
          <span>→</span>
        </button>

        <a routerLink="/faq" class="hero-button hero-button--secondary">
          Browse FAQs
        </a>
      </div>

      <div class="support-hero__meta">
        <span>Help for customers</span>
        <span class="support-hero__meta-dot"></span>
        <span>Salon owners</span>
        <span class="support-hero__meta-dot"></span>
        <span>Staff members</span>
      </div>
    </div>
  </section>

  <!-- Support options -->
  <section class="support-options">
    <div class="section-heading">
      <span class="section-kicker">Support options</span>
      <h2>Find the help you need.</h2>
      <p>Explore helpful resources or reach out to our team directly.</p>
    </div>

    <div class="support-options__grid">
      <article class="support-option-card">
        <div class="support-option-card__icon">
          <span>?</span>
        </div>

        <h3>Help Centre</h3>

        <p>
          Find answers to common questions about bookings, payments, accounts,
          and using gloget.
        </p>

        <a routerLink="/faq" class="support-option-card__link">
          Browse FAQs
          <span>→</span>
        </a>
      </article>

      <article class="support-option-card">
        <div
          class="support-option-card__icon support-option-card__icon--purple"
        >
          <span>✉</span>
        </div>

        <h3>Contact Support</h3>

        <p>
          Can't find the answer? Tell us what you're experiencing, and our team
          can assist.
        </p>

        <button
          type="button"
          class="support-option-card__link"
          (click)="scrollToForm()"
        >
          Create a request
          <span>→</span>
        </button>
      </article>

      <article class="support-option-card">
        <div class="support-option-card__icon support-option-card__icon--blue">
          <span>☷</span>
        </div>

        <h3>My Requests</h3>

        <p>View your support requests and check their current status.</p>

        <button
          type="button"
          class="support-option-card__link"
          (click)="viewRequests()"
        >
          View requests
          <span>→</span>
        </button>
      </article>
    </div>
  </section>

  <!-- Support workspace -->
  <section id="support-workspace" class="support-workspace">
    <div class="support-workspace__header">
      <div>
        <span class="section-kicker">We're here to help</span>
        <h2>Support requests</h2>
        <p>
          Send a message to the gloget team or review your existing requests.
        </p>
      </div>

      <div class="support-workspace__badge">Help &amp; Support</div>
    </div>

    <!-- Tabs -->
    <div class="support-tabs">
      <button
        type="button"
        class="support-tab"
        [class.support-tab--active]="activeTab === 'new'"
        (click)="setActiveTab('new')"
      >
        New Request
      </button>

      <button
        type="button"
        class="support-tab"
        [class.support-tab--active]="activeTab === 'requests'"
        (click)="setActiveTab('requests')"
      >
        My Requests

        <span
          *ngIf="isAuthenticated && tickets.length"
          class="support-tab__count"
        >
          {{ tickets.length }}
        </span>
      </button>
    </div>

    <!-- New request -->
    <div *ngIf="activeTab === 'new'" class="support-panel">
      <!-- Submission success -->
      <div *ngIf="submitted; else requestForm" class="support-success">
        <div class="support-success__icon">✓</div>

        <h3>Support request submitted</h3>

        <p>
          We've received your support request. A confirmation will be sent to
          your email address.
        </p>

        <div class="support-success__ticket">
          <span>Ticket reference</span>
          <strong>{{ submittedTicketId }}</strong>
        </div>

        <p class="support-success__notice">
          Please keep this reference for future correspondence.
        </p>

        <div class="support-success__actions">
          <button
            type="button"
            class="button-primary"
            (click)="createAnotherRequest()"
          >
            Create another request
          </button>

          <button
            *ngIf="isAuthenticated"
            type="button"
            class="button-secondary"
            (click)="viewRequests()"
          >
            View my requests
          </button>
        </div>
      </div>

      <!-- Request form -->
      <ng-template #requestForm>
        <div class="support-form-layout">
          <div class="support-form-main">
            <div class="support-form-heading">
              <h3>Submit a support request</h3>

              <p>
                Tell us a little about your issue so we can understand how to
                help.
              </p>
            </div>

            <div
              *ngIf="formError"
              class="form-alert form-alert--error"
              role="alert"
            >
              {{ formError }}
            </div>

            <form #supportForm="ngForm" (ngSubmit)="submitRequest()">
              <!-- Name and email -->
              <div class="form-row">
                <div class="form-field">
                  <label for="support-name"> Full name <span>*</span> </label>

                  <input
                    id="support-name"
                    name="name"
                    type="text"
                    [(ngModel)]="form.name"
                    placeholder="Enter your full name"
                    autocomplete="name"
                    minlength="2"
                    maxlength="150"
                    [readonly]="isAuthenticated && !!user?.full_name"
                    required
                  />
                </div>

                <div class="form-field">
                  <label for="support-email">
                    Email address <span>*</span>
                  </label>

                  <input
                    id="support-email"
                    name="email"
                    type="email"
                    [(ngModel)]="form.email"
                    placeholder="Enter your email"
                    autocomplete="email"
                    maxlength="254"
                    [readonly]="isAuthenticated"
                    required
                    email
                  />
                </div>
              </div>

              <!-- Category -->
              <div class="form-field">
                <label for="support-category">
                  What do you need help with? <span>*</span>
                </label>

                <select
                  id="support-category"
                  name="category"
                  [(ngModel)]="form.category"
                  required
                >
                  <option value="" disabled>Select a category</option>

                  <option
                    *ngFor="let category of categories"
                    [value]="category.value"
                  >
                    {{ category.label }}
                  </option>
                </select>
              </div>

              <!-- Subject -->
              <div class="form-field">
                <label for="support-subject"> Subject <span>*</span> </label>

                <input
                  id="support-subject"
                  name="subject"
                  type="text"
                  [(ngModel)]="form.subject"
                  placeholder="Briefly describe your issue"
                  minlength="5"
                  maxlength="200"
                  required
                />
              </div>

              <!-- Description -->
              <div class="form-field">
                <label for="support-description">
                  Description <span>*</span>
                </label>

                <textarea
                  id="support-description"
                  name="description"
                  [(ngModel)]="form.description"
                  rows="7"
                  minlength="10"
                  maxlength="5000"
                  placeholder="Please describe what happened, what you expected, and any relevant details..."
                  required
                ></textarea>

                <small>
                  Include any details that may help us understand your issue.
                </small>
              </div>

              <!-- Attachment -->
              <div class="form-field">
                <label for="support-attachment">
                  Attachment
                  <span class="optional">(Optional)</span>
                </label>

                <div class="attachment-box">
                  <input
                    id="support-attachment"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    (change)="onFileSelected($event)"
                  />

                  <p>
                    Upload a screenshot or document to help explain your issue.
                  </p>

                  <small> JPG, PNG, or PDF. Maximum 5 MB. </small>
                </div>

                <div *ngIf="selectedFile" class="attachment-selected">
                  <span>{{ selectedFile.name }}</span>

                  <button
                    type="button"
                    (click)="removeAttachment()"
                    aria-label="Remove attachment"
                  >
                    ×
                  </button>
                </div>

                <small *ngIf="attachmentError" class="field-error">
                  {{ attachmentError }}
                </small>
              </div>

              <!-- Footer -->
              <div class="support-form-footer">
                <p>
                  Please don't include passwords, verification codes, or full
                  payment card details.
                </p>

                <button
                  type="submit"
                  class="button-primary"
                  [disabled]="submitting || loadingUser || !isFormValid"
                >
                  {{ submitting ? "Submitting..." : "Submit Support Request" }}
                  <span>→</span>
                </button>
              </div>
            </form>
          </div>

          <!-- Sidebar -->
          <aside class="support-form-sidebar">
            <div class="support-info-card">
              <div class="support-info-card__icon">?</div>

              <h3>Before you submit</h3>

              <p>
                You might find the answer you're looking for in our Help Centre.
              </p>

              <a routerLink="/faq">
                Browse FAQs
                <span>→</span>
              </a>
            </div>

            <div class="support-tips-card">
              <h3>Helpful information</h3>

              <ul>
                <li>Describe the issue as clearly as possible.</li>
                <li>Include relevant appointment details when applicable.</li>
                <li>Attach a screenshot if it helps explain the problem.</li>
                <li>Never share passwords or full payment card details.</li>
              </ul>
            </div>
          </aside>
        </div>
      </ng-template>
    </div>

    <!-- My Requests -->
    <div *ngIf="activeTab === 'requests'" class="support-panel">
      <div class="support-requests-heading">
        <div>
          <h3>My Support Requests</h3>

          <p>
            Review your requests, track their progress, and respond to support
            messages.
          </p>
        </div>

        <button type="button" class="button-primary" (click)="scrollToForm()">
          New Request
          <span>+</span>
        </button>
      </div>

      <!-- Authentication loading -->
      <div *ngIf="loadingUser" class="support-empty">
        <div class="support-empty__icon">◷</div>
        <h3>Checking your account</h3>
        <p>Please wait while we load your support information.</p>
      </div>

      <!-- Guest message -->
      <div *ngIf="!loadingUser && !isAuthenticated" class="support-empty">
        <div class="support-empty__icon">♙</div>

        <h3>Sign in to view your requests</h3>

        <p>
          You can submit a support request without an account. To view your
          ticket history and conversations, please sign in to gloget.
        </p>

        <button type="button" class="button-primary" (click)="scrollToForm()">
          Create a support request
        </button>
      </div>

      <ng-container *ngIf="!loadingUser && isAuthenticated">
        <!-- Loading -->
        <div *ngIf="loadingTickets" class="support-empty">
          <div class="support-empty__icon">◷</div>
          <h3>Loading your requests</h3>
          <p>Please wait while we retrieve your support tickets.</p>
        </div>

        <!-- List error -->
        <div
          *ngIf="ticketsError && !loadingTickets"
          class="form-alert form-alert--error"
          role="alert"
        >
          {{ ticketsError }}

          <button
            type="button"
            class="button-secondary"
            (click)="loadSupportTickets()"
          >
            Try again
          </button>
        </div>

        <!-- Empty -->
        <div
          *ngIf="!loadingTickets && !ticketsError && tickets.length === 0"
          class="support-empty"
        >
          <div class="support-empty__icon">☷</div>

          <h3>No support requests yet</h3>

          <p>
            You haven't submitted any support requests while signed in to this
            account.
          </p>

          <button type="button" class="button-primary" (click)="scrollToForm()">
            Create a request
          </button>
        </div>

        <!-- Ticket list -->
        <div
          *ngIf="!loadingTickets && !ticketsError && tickets.length > 0"
          class="support-ticket-list"
        >
          <article
            *ngFor="let ticket of tickets; trackBy: trackByTicket"
            class="support-ticket"
          >
            <div class="support-ticket__main">
              <div class="support-ticket__top">
                <span class="support-ticket__id">
                  {{ ticket.reference }}
                </span>

                <span
                  class="support-ticket__status"
                  [ngClass]="'support-ticket__status--' + ticket.status"
                >
                  {{ getStatusLabel(ticket.status) }}
                </span>
              </div>

              <h4>{{ ticket.subject }}</h4>

              <p>{{ ticket.description }}</p>

              <div class="support-ticket__meta">
                <span>
                  {{ getCategoryLabel(ticket.category) }}
                </span>

                <span>•</span>

                <span>
                  {{ ticket.created_at | date: "mediumDate" }}
                </span>

                <span>•</span>

                <span>
                  {{ getPriorityLabel(ticket.priority) }}
                  Priority
                </span>
              </div>

              <button
                type="button"
                class="support-ticket__view"
                (click)="openTicket(ticket)"
              >
                View conversation
                <span>→</span>
              </button>
            </div>
          </article>
        </div>

        <!-- Ticket detail error -->
        <div
          *ngIf="ticketError"
          class="form-alert form-alert--error"
          role="alert"
        >
          {{ ticketError }}
        </div>

        <!-- Ticket detail loading -->
        <div *ngIf="loadingTicket" class="support-conversation">
          <p>Loading conversation...</p>
        </div>

        <!-- Ticket conversation -->
        <section
          *ngIf="selectedTicket && !loadingTicket"
          class="support-conversation"
        >
          <div class="support-conversation__header">
            <div>
              <span class="support-ticket__id">
                {{ selectedTicket.reference }}
              </span>

              <h3>{{ selectedTicket.subject }}</h3>

              <span
                class="support-ticket__status"
                [ngClass]="'support-ticket__status--' + selectedTicket.status"
              >
                {{ getStatusLabel(selectedTicket.status) }}
              </span>
            </div>

            <button
              type="button"
              class="button-secondary"
              (click)="closeTicketDetails()"
            >
              Close details
            </button>
          </div>

          <!-- Messages -->
          <div class="support-conversation__messages">
            <article
              *ngFor="
                let message of selectedTicket.messages;
                trackBy: trackByMessage
              "
              class="support-message"
              [class.support-message--staff]="message.is_staff_reply"
            >
              <div class="support-message__header">
                <strong>
                  {{
                    message.is_staff_reply
                      ? "gloget Support"
                      : message.sender_name
                  }}
                </strong>

                <span>
                  {{ message.created_at | date: "medium" }}
                </span>
              </div>

              <p>{{ message.body }}</p>

              <div
                *ngIf="message.attachments.length"
                class="support-message__attachments"
              >
                <button
                  *ngFor="let attachment of message.attachments"
                  type="button"
                  (click)="
                    downloadAttachment(
                      attachment.attachment_id,
                      attachment.original_filename
                    )
                  "
                >
                  ↓ {{ attachment.original_filename }}
                </button>
              </div>
            </article>
          </div>

          <!-- Reply form -->
          <div *ngIf="selectedTicket.status !== 'closed'" class="support-reply">
            <h4>Send a reply</h4>

            <div
              *ngIf="replyError"
              class="form-alert form-alert--error"
              role="alert"
            >
              {{ replyError }}
            </div>

            <textarea
              name="replyBody"
              [(ngModel)]="replyBody"
              rows="5"
              maxlength="10000"
              placeholder="Write your reply..."
            ></textarea>

            <div class="support-reply__attachment">
              <label for="support-reply-attachment">
                Attach a file (optional)
              </label>

              <input
                id="support-reply-attachment"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                (change)="onReplyFileSelected($event)"
              />

              <div *ngIf="replyAttachment" class="attachment-selected">
                <span>{{ replyAttachment.name }}</span>

                <button
                  type="button"
                  (click)="removeReplyAttachment()"
                  aria-label="Remove reply attachment"
                >
                  ×
                </button>
              </div>

              <small *ngIf="replyAttachmentError" class="field-error">
                {{ replyAttachmentError }}
              </small>
            </div>

            <button
              type="button"
              class="button-primary"
              [disabled]="!canReply"
              (click)="submitReply()"
            >
              {{ sendingReply ? "Sending..." : "Send Reply" }}
              <span>→</span>
            </button>
          </div>

          <div
            *ngIf="selectedTicket.status === 'closed'"
            class="support-conversation__closed"
          >
            This support request is closed.
          </div>
        </section>
      </ng-container>
    </div>
  </section>

  <!-- Bottom CTA -->
  <section class="support-bottom-cta">
    <div>
      <span class="section-kicker">Explore more answers</span>

      <h2>Looking for a quick solution?</h2>

      <p>
        Browse our frequently asked questions for helpful information about
        using gloget.
      </p>
    </div>

    <a routerLink="/faq" class="support-bottom-cta__button">
      Visit Help Centre
      <span>→</span>
    </a>
  </section>
</div>
